import { Inter } from "next/font/google";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { SignedIn, SignedOut } from "@clerk/nextjs";
import { BsHouseDoor, BsBuilding, BsMap } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";
import AuthDebugger from "../components/utils/AuthDebugger";
import ListingCard from "../components/listings/ListingCard.jsx";
import SearchResults from "../components/search/SearchResults.jsx";
import ShortletsCarousel from "../components/shortlets/ShortletsCarousel.jsx";
import { useSearch } from "../components/hooks/useSearch.js";
import { getPublicListings } from "../lib/public-listings";

// Make the font optional to prevent build failures when Google Fonts is unreachable
const inter = Inter({
  subsets: ["latin"],
  display: "swap",
  fallback: ["system-ui", "Arial", "sans-serif"],
  optional: true, // Add this line to make the font optional
});

export default function Home({
  featuredListings = [],
  fetchSuccess = true,
  error = null,
}) {
  // Search functionality
  const {
    searchParams,
    updateSearchParams,
    performSearch,
    results,
    fallbackResults,
    searchMeta,
    loading,
    error: searchError,
    pagination,
    loadMore,
  } = useSearch();

  const router = useRouter();
  const [showResults, setShowResults] = useState(false);

  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();

    // Check if any search criteria is provided
    const hasSearchCriteria =
      searchParams.query?.trim() ||
      searchParams.type ||
      searchParams.location ||
      searchParams.priceRange;

    // Always show results section and perform search
    setShowResults(true);

    if (hasSearchCriteria) {
      performSearch(searchParams); // Pass the current search parameters
    } else {
      // If no criteria provided, show featured listings as initial results
      console.log("No search criteria provided, showing featured listings");
      // The useSearch hook will handle showing fallback results
      performSearch({});
    }

    // Scroll to results section
    setTimeout(() => {
      const resultsSection = document.getElementById("search-results");
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  // Handle individual form field changes
  const handleFieldChange = (field, value) => {
    updateSearchParams({ [field]: value });

    // Removed auto-search - users must now click search button
    // Search will only be triggered by explicit search button click or Enter key
  };

  // Handle clear search
  const handleClearSearch = () => {
    updateSearchParams({
      query: "",
      type: "",
      location: "",
      priceRange: "",
    });
    setShowResults(false);
  };

  // Handle quick search from property categories
  const handleCategorySearch = (type) => {
    updateSearchParams({ type });
    setShowResults(true);
    performSearch({ type });

    // Scroll to results
    setTimeout(() => {
      const resultsSection = document.getElementById("search-results");
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  // Property categories with original images
  const categories = [
    {
      title: "Residential",
      description:
        "Find your dream home, from luxury apartments to family houses and lands.",
      image: "/residentialhm.jpg", // Use local image
      icon: <BsHouseDoor className="text-wine text-xl mr-2" />,
      link: "/listings?type=residential",
      type: "residential",
    },
    {
      title: "Commercial",
      description:
        "Office spaces, prime lands, retail locations, and other business properties.",
      image: "/comhm.jpg", // Use local image for commercial
      icon: <BsBuilding className="text-wine text-xl mr-2" />,
      link: "/listings?type=commercial",
      type: "commercial",
    },
    {
      // Display plural for marketing but search/type must match backend enum 'shortlet'
      title: "Shortlets",
      description: "Comfortable short–term stay options across Nigeria.",
      image: "/uploads/herobg.jpg", // Updated to use herobg.jpg from uploads folder
      icon: <BsMap className="text-wine text-xl mr-2" />,
      link: "https://shortlets.topdial.ng", // External dedicated shortlets site
      type: "shortlet",
      external: true,
    },
  ];

  return (
    <>
      <Head>
        <meta
          name="google-site-verification"
          content="hOQTKrppJZPzn5b0a7rHrdBhjwp8gpgV2NoiJ6qJ25I"
        />
      </Head>
      <main className="min-h-screen header-spacing">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center">
          <div className="absolute inset-0 z-0">
            <Image
              src="/homebg.jpg"
              alt="Modern real estate in Nigeria"
              fill
              priority
              sizes="100vw"
              className="object-cover brightness-[0.7]"
            />
          </div>

          <div className="container mx-auto px-4 z-10 relative">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Find Your Perfect Property in Nigeria
              </h1>
              <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                Topdial.ng connects you with the best properties across Nigeria.
                Buy, rent, or lease with confidence.
              </p>

              {/* Search Box - Mobile-Optimized */}
              <div className="w-full max-w-6xl mx-auto">
                <form
                  onSubmit={handleSearchSubmit}
                  className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6"
                >
                  {/* Mobile: Stack all fields vertically, Desktop: Flex layout */}
                  <div className="space-y-4 lg:space-y-0 lg:flex lg:gap-4 lg:items-end">
                    {/* Search Input - Full width on mobile, takes more space on desktop */}
                    <div className="w-full lg:flex-[2] relative">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Search Properties
                      </label>
                      <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                        <input
                          type="text"
                          placeholder="Search by location, title, or features..."
                          value={searchParams.query || ""}
                          onChange={(e) =>
                            handleFieldChange("query", e.target.value)
                          }
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-wine focus:border-wine outline-none transition-all text-gray-900 placeholder-gray-500"
                        />
                        {searchParams.query &&
                          searchParams.query.length > 0 &&
                          searchParams.query.length < 3 && (
                            <div className="absolute top-full left-0 right-0 mt-1 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-700 z-20">
                              Type at least 3 characters for suggestions
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Mobile: Two-column grid for selects, Desktop: Individual flex items */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:contents">
                      {/* Property Type */}
                      <div className="w-full lg:flex-1 lg:min-w-[140px]">
                        <select
                          value={searchParams.type || ""}
                          onChange={(e) =>
                            handleFieldChange("type", e.target.value)
                          }
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-wine focus:border-wine outline-none bg-white text-wine"
                        >
                          <option value="">All Types</option>
                          <option value="apartment">Apartment</option>
                          <option value="house">House</option>
                          <option value="commercial">Commercial</option>
                          <option value="land">Land</option>
                        </select>
                      </div>

                      {/* Location */}
                      <div className="w-full lg:flex-1 lg:min-w-[140px]">
                        <input
                          type="text"
                          placeholder="Enter city or area"
                          value={searchParams.location || ""}
                          onChange={(e) =>
                            handleFieldChange("location", e.target.value)
                          }
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-wine focus:border-wine outline-none text-gray-900 placeholder-gray-500"
                        />
                      </div>

                      {/* Price Range */}
                      <div className="w-full sm:col-span-2 lg:col-span-1 lg:flex-1 lg:min-w-[140px]">
                        <select
                          value={searchParams.priceRange || ""}
                          onChange={(e) =>
                            handleFieldChange("priceRange", e.target.value)
                          }
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-wine focus:border-wine outline-none bg-white text-wine"
                        >
                          <option value="">Any Price</option>
                          <option value="0-500000">Under ₦500K</option>
                          <option value="500000-1000000">₦500K - ₦1M</option>
                          <option value="1000000-5000000">₦1M - ₦5M</option>
                          <option value="5000000-10000000">₦5M - ₦10M</option>
                          <option value="10000000-50000000">₦10M - ₦50M</option>
                          <option value="50000000-">₦50M+</option>
                        </select>
                      </div>
                    </div>

                    {/* Search & Clear Buttons - Full width on mobile, side by side on larger screens */}
                    <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full sm:w-auto bg-wine text-white px-6 py-3 rounded-lg font-medium hover:bg-wine/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-h-[48px] lg:min-w-[120px]"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Searching...
                          </>
                        ) : (
                          <>
                            <FiSearch className="w-4 h-4 mr-2" />
                            Search
                          </>
                        )}
                      </button>

                      {/* Clear Button */}
                      {(searchParams.query ||
                        searchParams.type ||
                        searchParams.location ||
                        searchParams.priceRange) && (
                        <button
                          type="button"
                          onClick={handleClearSearch}
                          className="w-full sm:w-auto bg-gray-500 text-white px-4 py-3 rounded-lg font-medium hover:bg-gray-600 transition-colors min-h-[48px] whitespace-nowrap"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Property Categories */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2
              className="text-3xl font-bold text-center mb-12"
              style={{ color: "#232a37" }}
            >
              Browse by Property Type
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {categories.map((category, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-105 cursor-pointer"
                  onClick={() => {
                    if (category.external) {
                      // PWA-aware external link handling
                      const isPWA =
                        window.matchMedia("(display-mode: standalone)")
                          .matches ||
                        window.navigator.standalone ||
                        document.referrer.includes("android-app://");

                      if (isPWA) {
                        // For PWA, use window.location.href to avoid installation prompts
                        window.location.href = category.link;
                      } else {
                        // For regular browser, use new tab
                        const link = document.createElement("a");
                        link.href = category.link;
                        link.target = "_blank";
                        link.rel = "noopener noreferrer";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    } else {
                      handleCategorySearch(
                        category.type || category.title.toLowerCase()
                      );
                    }
                  }}
                >
                  <div className="h-48 relative">
                    <Image
                      src={category.image}
                      alt={`${category.title} Properties`}
                      fill
                      sizes="100vw"
                      className="object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center mb-3">
                      {category.icon}
                      <h3
                        className="text-xl font-semibold"
                        style={{ color: "#232a37" }}
                      >
                        {category.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-4">{category.description}</p>
                    <div className="flex space-x-3">
                      {!category.external && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCategorySearch(
                              category.type || category.title.toLowerCase()
                            );
                          }}
                          className="text-wine font-medium hover:underline"
                        >
                          Search Here →
                        </button>
                      )}
                      <Link
                        href={category.link}
                        className="text-gray-600 font-medium hover:underline"
                        onClick={(e) => e.stopPropagation()}
                        target={category.external ? "_blank" : undefined}
                        rel={
                          category.external ? "noopener noreferrer" : undefined
                        }
                      >
                        {category.external ? "Visit Site →" : "Browse All →"}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Shortlets */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h2 className="text-3xl font-bold" style={{ color: "#232a37" }}>
                  Featured Shortlets
                </h2>
                <p className="text-gray-600 mt-2">
                  Discover comfortable short-term stays across Nigeria
                </p>
              </div>
              <a
                href="https://shortlets.topdial.ng/listings"
                rel="noopener noreferrer"
                className="text-wine font-medium hover:underline flex items-center"
              >
                Browse All Shortlets →
              </a>
            </div>

            <ShortletsCarousel />
          </div>
        </section>

        {/* Featured Listings */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center mb-12">
              <h2 className="text-3xl font-bold" style={{ color: "#232a37" }}>
                Featured Properties
              </h2>
              <Link
                href="/listings"
                className="text-wine font-medium hover:underline"
              >
                View All Properties →
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredListings.length === 0 ? (
                <div className="col-span-3 text-center text-gray-500 p-12 bg-white rounded-lg shadow-sm">
                  {!fetchSuccess ? (
                    <>
                      <p className="mb-4 font-medium">
                        Temporarily unable to load featured listings
                      </p>
                      <p className="text-sm">
                        Our team has been notified. Please check back later.
                      </p>
                    </>
                  ) : (
                    <p>No featured properties found at this time.</p>
                  )}
                </div>
              ) : (
                featuredListings.map((listing) => (
                  <ListingCard key={listing._id} listing={listing} />
                ))
              )}
            </div>

            {/* Debug Info - Will only show in development */}
            {process.env.NODE_ENV === "development" && (
              <div className="mt-8 p-4 border border-gray-300 rounded bg-gray-50">
                <h4 className="text-sm font-semibold mb-2">
                  Debug Information:
                </h4>
                <p className="text-xs text-gray-700">
                  Featured Listings Loaded: {featuredListings.length}
                </p>
                <p className="text-xs text-gray-700">
                  Fetch Success: {fetchSuccess ? "Yes" : "No"}
                </p>
                {error && (
                  <p className="text-xs text-red-900">Error: {error}</p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Search Results Section */}
        {showResults && (
          <section id="search-results" className="py-16 bg-white">
            <div className="container mx-auto px-4">
              {/* Search Summary */}
              <div className="mb-8 text-center">
                <h2
                  className="text-2xl font-bold mb-2"
                  style={{ color: "#232a37" }}
                >
                  Search Results
                </h2>
                {searchParams.query ||
                searchParams.type ||
                searchParams.location ||
                searchParams.priceRange ? (
                  <p className="text-gray-600">
                    {searchParams.query &&
                      `Searching for "${searchParams.query}"`}
                    {searchParams.type && ` • Type: ${searchParams.type}`}
                    {searchParams.location &&
                      ` • Location: ${searchParams.location}`}
                    {searchParams.priceRange &&
                      ` • Price: ${searchParams.priceRange}`}
                  </p>
                ) : (
                  <p className="text-gray-600">Showing recent properties</p>
                )}
                <button
                  onClick={handleClearSearch}
                  className="mt-2 text-wine hover:underline text-sm"
                >
                  Clear search and return to homepage
                </button>
              </div>

              <SearchResults
                results={results}
                fallbackResults={fallbackResults}
                searchMeta={searchMeta}
                loading={loading}
                error={searchError}
                pagination={pagination}
                onLoadMore={loadMore}
                searchParams={searchParams}
              />
            </div>
          </section>
        )}

        {/* Why Choose Us */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2
              className="text-3xl font-bold text-center mb-12"
              style={{ color: "#232a37" }}
            >
              Why Choose Topdial.ng
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="bg-red-50 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-wine"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                    />
                  </svg>
                </div>
                <h3
                  className="text-xl font-semibold mb-3"
                  style={{ color: "#232a37" }}
                >
                  Verified Properties
                </h3>
                <p className="text-gray-600">
                  All our listings are verified by our team to ensure legitimacy
                  and quality.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="bg-red-50 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-wine"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                    />
                  </svg>
                </div>
                <h3
                  className="text-xl font-semibold mb-3"
                  style={{ color: "#232a37" }}
                >
                  Expert Team
                </h3>
                <p className="text-gray-600">
                  Our network of professionals provide personalized guidance.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="bg-red-50 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                  {/* New icon: User/Check for vetted tenants */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-wine"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.25v-1.5A2.25 2.25 0 016.75 16.5h10.5a2.25 2.25 0 012.25 2.25v1.5M17 9l2 2 4-4"
                    />
                  </svg>
                </div>
                <h3
                  className="text-xl font-semibold mb-3"
                  style={{ color: "#232a37" }}
                >
                  Background Checks
                </h3>
                <p className="text-gray-600">
                  Get access to properly vetted tenants.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-wine text-white">
          <div className="container mx-auto px-4 text-center">
            <h2
              className="text-3xl font-bold mb-4"
              style={{ color: "#FFFFFF" }}
            >
              Ready to find your dream property?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Whether you're looking to buy, rent, or invest, Topdial.ng has the
              perfect property for you.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/listings"
                className="bg-white text-wine px-8 py-3 rounded-md font-medium hover:bg-gray-100"
              >
                Browse Properties
              </Link>
              <SignedOut>
                <Link
                  href="/auth/sign-up"
                  className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-md font-medium hover:bg-white hover:text-wine"
                >
                  Sign Up Now
                </Link>
              </SignedOut>
              <SignedIn>
                <Link
                  href="/dashboard"
                  className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-md font-medium hover:bg-white hover:text-wine"
                >
                  Go to Dashboard
                </Link>
              </SignedIn>
            </div>
          </div>
        </section>
      </main>
      {/* Add auth debugger in development */}
      <AuthDebugger />
    </>
  );
}

// Use server-side rendering to fetch listings on each request
export async function getServerSideProps() {
  try {
    // INVESTIGATION: Log SSR environment details
    console.log("🔍 [SSR DEBUG] Environment variables:");
    console.log(
      "  - NEXT_PUBLIC_APP_URL:",
      process.env.NEXT_PUBLIC_APP_URL || "undefined"
    );
    console.log("  - NODE_ENV:", process.env.NODE_ENV);
    console.log("  - MONGODB_URI exists:", !!process.env.MONGODB_URI);

    // Fetch the 3 featured listings with proper sorting
    // Use featured sort to prioritize isFeatured=true listings
    const { listings, success } = await getPublicListings(
      { sort: "featured" }, // Sort by featured first, then creation date
      1,
      3
    );

    // Log successful fetching
    console.log(
      `🔍 [SSR RESULT] Homepage SSR: Fetched ${listings?.length || 0} featured listings`
    );
    console.log("🔍 [SSR RESULT] Success flag:", success);

    // Log for debugging if no listings found
    if (!listings || listings.length === 0) {
      console.warn(
        "🔍 [SSR WARNING] No listings found. This may indicate an issue with database connection or empty database."
      );
    } else {
      console.log(
        "🔍 [SSR RESULT] Sample listing titles:",
        listings.map((l) => l.title || "Untitled").slice(0, 3)
      );
    }

    return {
      props: {
        featuredListings: listings || [],
        fetchSuccess: success || false,
      },
    };
  } catch (error) {
    console.error("Homepage SSR error:", error);

    // Return empty listings on error
    return {
      props: {
        featuredListings: [],
        fetchSuccess: false,
        error:
          process.env.NODE_ENV === "development"
            ? error.message
            : "Failed to load listings",
      },
    };
  }
}
